<template>
  <div :class="['gam-gambit-simple-tags', { 'horizontal-padding': isHorizontallyPadded }]">
    <ScrollOverflow v-if="gambit" class="gambit-tag-container" :is-horizontal="true">
      <GamChip
        v-if="showNeedPlayers && gambit.attendeeCount === 0"
        :left-icon="GamIconName.PAWN_EMPTY"
        class="need-players"
        :size="size"
      >
        Need players
      </GamChip>

      <gam-chip
        v-for="(tag, index) in gambitTags"
        v-bind="tag"
        :key="`tag-${index}`"
        :is-active="isChipActive(gambit as BaseGambitDto, tag, index)"
      />
    </ScrollOverflow>
  </div>
</template>

<script setup lang="ts">
import type { BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import { GambitCategory } from '@/core/data/gambit/gambit.type';
import type { NextGambitDto } from '@/core/data/location/location.interface';
import { localize } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamChip from '@/views/components/GamChip.vue';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import ScrollOverflow from '../ScrollOverflow.vue';

export interface GambitSimpleTagsProps {
  gambit?: BaseGambitDto | GambitDetailDto | NextGambitDto | null;
  isHorizontallyPadded?: boolean;
  // tagOptions
  isShort?: boolean;
  size?: GamChipSize;
  showChallenge?: boolean;
  showSpark?: boolean;
  showAttendees?: boolean;
  showConfirmations?: boolean;
  showRating?: boolean;
  showCategory?: boolean;
  showChessSets?: boolean;
  showTypes?: boolean;
  showAny?: boolean;
  showRecurring?: boolean;
  showNeedPlayers?: boolean;
  highlightAttendance?: boolean;
  respondBadge?: 'challenge' | 'invitation';
}

const props = withDefaults(defineProps<GambitSimpleTagsProps>(), {
  gambit: null,
  size: GamChipSize.SMALL,
  showAttendees: true,
  showRating: true,
  showCategory: true,
  showTypes: true,
  showRecurring: true,
  respondBadge: undefined,
});

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const isChallenger = computed(() => {
  if (!props.gambit || !userDetails.value) return false;
  return (props.gambit as BaseGambitDto).ownerId === userDetails.value.id;
});

const isChipActive = (gambit: BaseGambitDto, tag: GamChipType, index: number) => {
  return (
    (gambit.attendeeCount >= 1 && index < (gambit.maxEloRating ? 2 : 1)) ||
    (gambit.gambitCategory === GambitCategory.SPARK && !gambit.isConfirmed)
  );
};

const gambitTags = computed((): GamChipType[] => {
  if (!props.gambit) return [];

  // Exclude respondBadge from tagOptions — it's handled separately below as a prepended chip
  const { gambit, respondBadge, ...tagOptions } = props;
  const { highlightAttendance } = tagOptions;

  const tags = GambitHelper.getGambitsTags(gambit as BaseGambitDto, {
    ...tagOptions,
    size: props.size ?? GamChipSize.SMALL,
    recurrenceCount: (gambit as GambitDetailDto).futureRecurrences?.length || 0,
    highlightAttendance: highlightAttendance,
    isChallenger: isChallenger.value,
  });

  if (respondBadge) {
    let label = localize('player.sections.respond.badge.invitation');
    if (respondBadge === 'challenge') {
      label = (gambit as BaseGambitDto).isEditedChallenge
        ? localize('gambit.challenge.edited')
        : localize('player.sections.respond.badge.challenge');
    }
    tags.unshift(
      GambitHelper.gambitTag(label, {
        icon: GamIconName.PAWN,
        variant: GamChipVariant.PRIMARY,
        size: props.size,
      }),
    );
  }

  return tags;
});
</script>

<style lang="scss" scoped>
.need-players {
  text-transform: uppercase;
}

.gam-gambit-simple-tags {
  display: flex;
  width: 100%;
  align-items: center;
  min-width: 0;

  .tags-icon {
    align-self: center;
  }

  .gambit-tag-container {
    align-items: center;
    position: relative;
    align-items: center;
    width: 100%;
    display: flex;
    overflow: scroll hidden;
    scrollbar-width: none;
    gap: var(--spacing-md);
  }

  &.horizontal-padding {
    .gambit-tag-container {
      padding-left: var(--spacing-l);
      padding-right: var(--spacing-l);
    }
  }
}
</style>
